import { FirebaseAppFirestore } from '@/firebase/firebase-app';
import firebaseNames from '@/statics/firebase-names';
import { AdSpace } from '@/types/adspaces';
import { getCurrentUser } from '@/firebase/firebase-user';
import { FILTER_QUERY_LIMIT } from '@/statics/main';
import { locationDataFactory } from '@/store/modules/common-module/actions';
import { chunk, flow, uniqBy, map, flatten } from 'lodash';
import { AuthorizedUser } from '@/types/users';
import { StoreOwnerLocationData } from '@/types/locations';
import { StoreAccount } from '@/types/store';

/**
 * Get all Ad Spaces info
 */
export const getOnlineClusteredAdSpaces = async (): Promise<AdSpace[]> => {
  const collectionRef = FirebaseAppFirestore
    .collection(firebaseNames.AD_SPACES)
    .where('IS_ONLINE','==',true)
    .orderBy('SCREEN_CLUSTER_REF');

  const adSpacesSnap = await collectionRef.get()

  if (adSpacesSnap.empty) {
    return [];
  }

 return adSpacesSnap.docs.map((doc) => doc.data() as AdSpace);
};
/**
 * Get screen cluster refs from regulatorId
 */
export const getAdSpacesByRegulatorId = async (regulatorId: AuthorizedUser['UID']): Promise<AdSpace[]|undefined> => {
  const collectionRef = FirebaseAppFirestore
    .collection(firebaseNames.AD_SPACES)
    .where('REGULATOR_ID', '==', regulatorId)

  const adSpacesSnap = await collectionRef.get()

  if (adSpacesSnap.empty) {
    return;
  }
 return adSpacesSnap.docs.map((doc) => doc.data() as AdSpace);
};

export const getClusteredAdSpacesByStoreIds = async (spaceOwnersIds: (AdSpace['SPACE_OWNER_ID'])[]): Promise<AdSpace[]> => {
  const collectionRef = FirebaseAppFirestore
    .collection(firebaseNames.AD_SPACES)
    .where('SPACE_OWNER_ID', 'in', spaceOwnersIds)
    .orderBy('SCREEN_CLUSTER_REF');

  const adSpacesSnap = await collectionRef.get()

  if (adSpacesSnap.empty) {
    return [];
  }

  return adSpacesSnap.docs.map((doc) => doc.data() as AdSpace);
}
export const loadStoreOwnerLocationsAction = async () => {
  const currentUser = await getCurrentUser();
  const collectionRef = FirebaseAppFirestore
    .collection(firebaseNames.AD_SPACES)
    .where('SPACE_OWNER_ID', '==', currentUser?.uid || '')
    .orderBy('SCREEN_CLUSTER_REF');

  const adSpacesSnap = await collectionRef.get()

  if (adSpacesSnap.empty) {
    return [];
  }
  const adSpaces = adSpacesSnap.docs.map((doc) => doc.data() as AdSpace);
  const regulators = flow((adSpacesData) => adSpacesData.filter((adSpace: AdSpace) => adSpace.REGULATOR_ID),
    (adSpacesWithRegulators) => uniqBy(adSpacesWithRegulators, 'REGULATOR_ID'),
    (uniqueAdSpacesWithRegulators: AdSpace[]) => uniqueAdSpacesWithRegulators.map((adSpace) => adSpace.REGULATOR_ID))
  (adSpaces as AdSpace[]);

  const regulatorNames = await flow(
    (regulatorsIds) => chunk(regulatorsIds, FILTER_QUERY_LIMIT),
    (chunkedRegulatorsIds) => {
      return map(chunkedRegulatorsIds, (filterChunk) =>
        FirebaseAppFirestore
          .collection(firebaseNames.REGULATORS)
          .where('UID', 'in', filterChunk)
          .get())
    },
    async (snapshots) => {
      const allData = await Promise.all(snapshots)
      const regulatorDataArray = flatten(allData.map((snapshot) => snapshot.docs.map((doc) => doc.data() as AuthorizedUser)));
      return regulatorDataArray.reduce((regulatorMap, regulator) => ({
        ...regulatorMap,
        [regulator.UID]: regulator.NAME
      }), {} as Record<AuthorizedUser['UID'], AuthorizedUser['NAME']>)
    }
  )(regulators)
  const locationsData = locationDataFactory(adSpaces);
  return map(locationsData, (locationData) => ({
    ...locationData,
    regulatorName: regulatorNames?.[locationData?.REGULATOR_ID || ''] || ''
  })) as StoreOwnerLocationData[]
}

export const getFrequencySliderEnabledForStoreOwner = async (userId: AuthorizedUser['UID'])
  : Promise<NonNullable<StoreAccount['ENABLE_FREQUENCY_SLIDER']>> => {
  const storeAccountSnap = await (FirebaseAppFirestore
    .collection(firebaseNames.STORES)
    .doc(userId).get());
  return Boolean((storeAccountSnap.data() as StoreAccount)?.ENABLE_FREQUENCY_SLIDER);
}
